import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import HeroBackdrop from "@/components/HeroBackdrop";
import WhyLeadersSection from "@/components/WhyLeadersSection";
import { AlchamisMark } from "@/components/AlchamisMark";
import { CASE_STUDIES, CONSULTING_SERVICES, INSIGHTS, SOLUTIONS, SEO_DEFAULTS } from "@/data/content";
import { INDUSTRY_PAGES } from "@/data/industries";

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService"],
  name: "AlchamisGroup",
  alternateName: "Alchamis Group",
  url: SEO_DEFAULTS.baseUrl,
  logo: SEO_DEFAULTS.logoUrl,
  image: SEO_DEFAULTS.defaultImage,
  description:
    "Global consulting and technology group delivering AI consulting, digital transformation, operational excellence, compliance, and enterprise software.",
  founder: { "@type": "Person", name: "Latifa Richards" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "90 E Halsey Rd, Suite 332",
    addressLocality: "Parsippany",
    addressRegion: "NJ",
    postalCode: "07054",
    addressCountry: "US",
  },
  telephone: "+1-973-441-0708",
  email: "admin@alchamisgroup.com",
  areaServed: "Global",
  serviceType: [
    "AI Consulting",
    "Digital Transformation",
    "Operational Excellence",
    "Business Process Optimization",
    "Compliance & Governance",
    "Enterprise Software",
    "Product Lifecycle Management",
    "Customer Relationship Management",
    "AI Adoption",
  ],
};

function HomeHero() {
  return (
    <section data-testid="home-hero" className="relative min-h-screen flex items-end overflow-hidden">
      <HeroBackdrop />
      <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-12 pt-32 pb-20 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-8">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-4 mb-10"
          >
            <span className="number-badge">EST. 2017 · PARSIPPANY · NEW JERSEY</span>
            <span className="h-px w-16 bg-gold/40" />
            <span className="overline">Global Consulting &amp; Technology</span>
          </motion.div>

          <motion.h1
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="font-display text-[2.6rem] sm:text-6xl lg:text-[5.4rem] leading-[1.02] text-white text-balance"
          >
            AI consulting and enterprise software,
            <span className="block italic gold-text">delivered with institutional discipline.</span>
          </motion.h1>

          <motion.p
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.25 }}
            className="mt-10 text-white/70 text-base sm:text-lg max-w-2xl leading-relaxed"
          >
            AlchamisGroup advises boards and operating teams on AI adoption, digital transformation,
            operational excellence, and compliance — supported by our own enterprise platforms across
            PLM, CRM, and category software. Partner-led from day one.
          </motion.p>

          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <Link to="/ai-advisor" data-testid="hero-cta-advisor" className="luxury-button-gold">
              Begin AI Diagnostic <ArrowRight size={14} />
            </Link>
            <Link to="/services" data-testid="hero-cta-services" className="luxury-button-ghost-dark">
              Explore Practices
            </Link>
          </motion.div>
        </div>

        <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.4 }}
            className="self-end relative w-full max-w-sm"
          >
            <div className="glass-dark p-8">
              <div className="overline mb-4">Executive AI Diagnostic</div>
              <div className="font-display text-3xl text-white leading-tight mb-6">
                A board-grade AI readiness memo, generated in minutes.
              </div>
              <ul className="space-y-3 text-sm text-white/70">
                <li className="flex justify-between"><span>Business maturity</span><span className="text-gold">Scored</span></li>
                <li className="flex justify-between"><span>Digital transformation</span><span className="text-gold">Scored</span></li>
                <li className="flex justify-between"><span>AI readiness</span><span className="text-gold">Scored</span></li>
                <li className="flex justify-between"><span>Recommended practices</span><span className="text-gold">Curated</span></li>
              </ul>
              <Link to="/ai-advisor" className="mt-6 inline-flex items-center gap-2 text-gold text-sm tracking-widest uppercase hover:text-gold-pale">
                Take Diagnostic <ArrowUpRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-4 inset-x-0 flex justify-center">
        <div className="text-[0.6rem] tracking-[0.4em] text-white/40 uppercase animate-pulse-gold">
          Scroll · Discover
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    "AI Consulting",
    "Digital Transformation",
    "Operational Excellence",
    "Compliance & Governance",
    "Enterprise PLM",
    "Enterprise CRM",
    "AI Adoption",
  ];
  return (
    <section className="border-y border-[#001B54]/10 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center gap-x-10 gap-y-3 justify-center">
        {items.map((i) => (
          <span key={i} className="text-[0.65rem] tracking-[0.3em] uppercase text-[#001B54]/55">{i}</span>
        ))}
      </div>
    </section>
  );
}

function ManifestoSection() {
  return (
    <section className="section-pad bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-4">
          <span className="overline">01 — Firm</span>
          <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-[1.05]">
            A consulting firm,
            <span className="block italic gold-text">engineered like a technology group.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-7 lg:col-start-6 space-y-6 text-[#3F4A6B] text-base sm:text-lg leading-relaxed">
          <p>
            AlchamisGroup is the rare advisory house that delivers strategy and engineering under
            one partnership. Top-tier consultants. Operator-grade engineers. One accountable
            partner from intake to adoption.
          </p>
          <p>
            We sit alongside CEOs, boards, and operating partners on the decisions that compound:
            AI adoption posture, digital transformation portfolios, operational re-architecture,
            and the enterprise platforms that make those choices durable.
          </p>
          <div className="grid grid-cols-3 gap-6 pt-6 border-t border-[#001B54]/12">
            {[
              { value: "$3.4B+", label: "Client revenue under advisement" },
              { value: "42", label: "Industries served globally" },
              { value: "100%", label: "Engagements led by partner-level talent" },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-display text-3xl gold-text">{s.value}</div>
                <div className="text-xs text-[#6B7390] tracking-wide mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const featured = CONSULTING_SERVICES.slice(0, 6);
  return (
    <section className="section-pad bg-[#FAFAF6]" data-testid="home-services">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div className="max-w-2xl">
            <span className="overline">02 — Practices</span>
            <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-[1.05]">
              Strategy and execution
              <span className="italic gold-text"> under one partnership.</span>
            </h2>
          </div>
          <Link to="/services" className="text-[#B8901C] text-xs tracking-[0.22em] uppercase hover:text-[#9F7E22] inline-flex items-center gap-2">
            All 15 Practices <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#001B54]/10">
          {featured.map((s) => (
            <Link
              to={`/services/${s.slug}`}
              key={s.code}
              data-testid={`service-card-${s.slug}`}
              className="group border-r border-b border-[#001B54]/10 p-8 lg:p-10 bg-white hover:bg-[#FFFCF4] transition-colors duration-500"
            >
              <div className="flex items-center justify-between">
                <div className="number-badge">{s.code}</div>
                <ArrowUpRight size={14} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <h3 className="font-display text-2xl text-[#001B54] mt-6 mb-4 leading-tight">{s.name}</h3>
              <p className="text-[#3F4A6B] text-sm leading-relaxed">{s.summary}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function PortfolioSection() {
  return (
    <section className="section-pad bg-[#FAFAF6] relative overflow-hidden" data-testid="home-portfolio">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 lg:col-span-6">
            <span className="overline">03 — Platforms</span>
            <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-[1.05]">
              Four enterprise platforms,
              <span className="block italic gold-text">one institutional standard.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 text-[#3F4A6B] text-base leading-relaxed">
            <p>
              The AlchamisGroup platform portfolio is engineered by the same partners and architects who
              lead our consulting work — Product Lifecycle Management, Customer Relationship Management,
              and category platforms built for institutions that consider their software a strategic asset.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SOLUTIONS.map((s) => (
            <Link
              key={s.slug}
              to={`/platforms/${s.slug}`}
              data-testid={`portfolio-card-${s.slug}`}
              className="group relative overflow-hidden border border-[#001B54]/10 hover:border-gold/60 transition-all duration-500 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.15)]"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.name}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#001B54] via-[#001B54]/70 to-transparent" />
              </div>
              <div className="absolute inset-0 p-8 lg:p-10 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="number-badge text-[#F4D88A]">{s.ventureCode}</span>
                  <ArrowUpRight size={18} className="text-gold opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-500" />
                </div>
                <div>
                  <h3 className="font-display text-3xl lg:text-4xl text-white mb-2">{s.name}</h3>
                  <p className="text-gold/90 text-xs tracking-[0.18em] uppercase mb-4">{s.tagline}</p>
                  <p className="text-white/75 text-sm leading-relaxed max-w-md">{s.mission}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function AIAdvisorTeaser() {
  return (
    <section className="section-pad relative overflow-hidden" data-testid="home-ai-teaser">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1685625971503-ef1f82128939?auto=format&fit=crop&w=1800&q=80"
          alt=""
          loading="lazy"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000B24] via-[#000B24]/80 to-transparent" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-7">
          <span className="overline">04 — Executive AI Diagnostic</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-6 leading-[1.05]">
            An AI Readiness Memo for boards
            <span className="block italic gold-text">and operating committees.</span>
          </h2>
          <p className="mt-8 text-white/70 max-w-xl text-base sm:text-lg leading-relaxed">
            Six minutes of executive intake. Calibrated scoring across business maturity, digital
            transformation, and AI readiness. A McKinsey-grade memo delivered to your inbox — plus a
            downloadable PDF for your board pack.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/ai-advisor" data-testid="ai-teaser-cta" className="luxury-button-gold">
              Begin Diagnostic <ArrowRight size={14} />
            </Link>
            <Link to="/ai-services" className="luxury-button-ghost-dark">Explore AI Practice</Link>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 lg:col-start-9 flex items-end">
          <div className="w-full glass-dark p-8 space-y-4">
            <div className="overline">Memo Includes</div>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="border-b border-white/10 pb-3">Business Maturity Score (0–100)</li>
              <li className="border-b border-white/10 pb-3">Digital Transformation Score</li>
              <li className="border-b border-white/10 pb-3">AI Readiness Score</li>
              <li className="border-b border-white/10 pb-3">Strategic Priorities &amp; Risks</li>
              <li>Recommended Practices &amp; Platforms</li>
            </ul>
            <div className="text-xs text-gold/80 tracking-wider pt-2">Downloadable Executive PDF</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CaseStudiesStrip() {
  const top = CASE_STUDIES.slice(0, 3);
  return (
    <section className="section-pad bg-[#FAFAF6]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <span className="overline">05 — Engagements</span>
            <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-tight">Selected outcomes.</h2>
          </div>
          <Link to="/case-studies" className="text-[#B8901C] text-xs tracking-[0.22em] uppercase hover:text-[#9F7E22] inline-flex items-center gap-2">
            All Engagements <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {top.map((c) => (
            <Link to={`/case-studies/${c.slug}`} key={c.slug} className="group bg-white border border-[#001B54]/10 hover:border-gold/60 transition-all duration-500 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.15)]">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={c.image} alt={c.headline} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-all duration-1000" />
              </div>
              <div className="p-6">
                <div className="text-[0.6rem] tracking-[0.28em] uppercase text-[#B8901C] mb-2">{c.industry}</div>
                <div className="font-display text-2xl text-[#001B54] leading-tight">{c.headline}</div>
                <div className="mt-4 flex gap-5">
                  {c.outcomes.slice(0, 2).map((o) => (
                    <div key={o.label}>
                      <div className="font-display text-2xl gold-text">{o.value}</div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-[#6B7390] mt-1">{o.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndustriesSection() {
  return (
    <section className="section-pad bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <span className="overline">06 — Industries</span>
          <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-[1.05]">
            Sectors we know
            <span className="block italic gold-text">as well as our own.</span>
          </h2>
          <p className="text-[#3F4A6B] mt-6 max-w-md text-base leading-relaxed">
            Our engagements are led by partners with operating careers inside the industries we serve.
          </p>
          <Link to="/industries" className="inline-flex items-center gap-2 mt-6 text-[#B8901C] text-xs tracking-[0.22em] uppercase hover:text-[#9F7E22]">
            All Industries <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="col-span-12 lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#001B54]/10">
          {INDUSTRY_PAGES.slice(0, 9).map((i) => (
            <Link
              key={i.slug}
              to={`/industries/${i.slug}`}
              className="bg-white p-6 lg:p-8 hover:bg-[#FFFCF4] transition-colors group"
            >
              <div className="w-10 h-10 bg-[#001B54] flex items-center justify-center">
                <AlchamisMark size={20} />
              </div>
              <div className="text-[#001B54] text-sm mt-4 group-hover:text-[#B8901C] transition-colors font-medium">{i.name}</div>
              <div className="text-[#6B7390] text-xs mt-1">{i.eyebrow}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function InsightsSection() {
  return (
    <section className="section-pad bg-[#FAFAF6]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap mb-12 gap-6">
          <div>
            <span className="overline">07 — Insights</span>
            <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-tight">
              Selected perspectives.
            </h2>
          </div>
          <Link to="/insights" className="text-[#B8901C] text-xs tracking-[0.22em] uppercase hover:text-[#9F7E22] inline-flex items-center gap-2">
            All Insights <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {INSIGHTS.map((i) => (
            <Link to={`/insights/${i.id}`} key={i.id} className="group block bg-white border border-[#001B54]/10 hover:border-gold/60 transition-all duration-500 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.15)]">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={i.image} alt={i.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-all duration-1000" />
              </div>
              <div className="p-6">
                <div className="text-[0.6rem] tracking-[0.28em] uppercase text-[#B8901C] mb-2">{i.category} · {i.readTime}</div>
                <div className="font-display text-2xl text-[#001B54] leading-tight">{i.title}</div>
                <div className="text-[#3F4A6B] text-sm mt-3 leading-relaxed">{i.summary}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="bg-[#001B54] section-pad relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 50% 100%, rgba(212,175,55,0.25), transparent 60%)",
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 text-center">
        <span className="overline" style={{ color: "#F4D88A" }}>A private engagement</span>
        <h2 className="font-display text-4xl sm:text-6xl text-white mt-6 leading-[1.05] text-balance">
          When the next decision matters,
          <span className="block italic gold-text">choose your counsel deliberately.</span>
        </h2>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/contact" className="luxury-button-gold" data-testid="footer-cta-contact">
            Request a private briefing <ArrowRight size={14} />
          </Link>
          <Link to="/ai-advisor" className="luxury-button-ghost-dark">Take the diagnostic</Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="AI Consulting & Enterprise Software"
        description="AI consulting, digital transformation, PLM, CRM, and AI adoption — partner-led from board strategy to engineering delivery."
        path="/"
        jsonLd={ORG_JSONLD}
      />
      <HomeHero />
      <TrustBar />
      <WhyLeadersSection />
      <ManifestoSection />
      <ServicesSection />
      <PortfolioSection />
      <AIAdvisorTeaser />
      <CaseStudiesStrip />
      <IndustriesSection />
      <InsightsSection />
      <CTASection />
    </div>
  );
}
